import {Injectable} from '@angular/core';
import {Router} from "@angular/router";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";
import {environment} from '../../environments/environment';
import {User} from '../_types/user';
import {GenericResponseWithPagination} from '../_types/shared';
import {Question, QuestionAnswer} from '../_types/communities';

interface LoginResponse {
    token: string;
    email: string;
    id: number;
    user: User;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private user: User | null = null;

    constructor(
        private router: Router,
        private http: HttpClient,
    ) {
    }

    login(body: {
        email: string,
        password: string,
        community_id?: number,
        keep_logged_in?: boolean
    }): Observable<User> {
        localStorage.removeItem('hijacked');
        return this.http.post<User>(`${environment.apiUrl}/users/login/`, body);
    }

    // After login save token and other values(if any) in localStorage
    setUser(resp: LoginResponse): void {
        localStorage.setItem('access_token', resp.token);
        localStorage.setItem('currentUser', JSON.stringify(resp.user));
    }

    patchUser(body: any, file?: any): Observable<any> {
        const headerAttachmentNameValue: string = 'attachment; filename="profile_picture"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };
        const fd: FormData = new FormData();

        for (const [key, value] of Object.entries(body)) {
            if (typeof value == 'string') {
                fd.append(key, value);
            } else {
                fd.append(key, JSON.stringify(value));
            }
        }

        if (file) {
            fd.append('profile_picture', file);
        }
        return this.http.patch<any>(`${environment.apiUrl}/users/`, fd, {
            headers
        });
    }

    // Checking if token is set
    isLoggedIn(): boolean {
        return localStorage.getItem('access_token') != null;
    }

    // After clearing localStorage redirect to login screen
    logout(returnUrl?: string): void {
        localStorage.removeItem('hijacked');
        localStorage.removeItem('access_token');
        localStorage.removeItem('currentUser');
        this.user = null;
        if (returnUrl) {
            this.router.navigate(['/login'], {queryParams: {returnUrl: returnUrl}});
        } else {
            this.router.navigate(['/login']);
        }
    }

    getUser(): any {
        return this.user ?? JSON.parse(localStorage.getItem('currentUser') as string);
    }

    async refreshUser(): Promise<User | null> {
        return new Promise((resolve, reject): void => {
            this.http.get<any>(`${environment.apiUrl}/users/`).subscribe(r => {
                localStorage.setItem('currentUser', JSON.stringify(r));
                resolve(r)
            }, e => {
                reject(null)
                console.error(e);
            });
        });
    }

    isSuperUser(): boolean {
        return this.getUser()?.['is_superuser'];
    }

    requestPasswordReset(email: string, community_id?: number): Observable<User> {
        localStorage.removeItem('hijacked');
        return this.http.post<User>(`${environment.apiUrl}/users/request-password-reset/`, {email, community_id});
    }

    resetPassword(token: string, body: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/users/reset-password/${token}/`, body);
    }

    createUser(body: any, file?: any, inviteToken?: string): Observable<any> {
        let params = new HttpParams();
        if (inviteToken) {
            params = params.append('invite_token', inviteToken);
        }

        const headerAttachmentNameValue: string = 'attachment; filename="profile_picture"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };
        const fd: FormData = new FormData();

        for (const [key, value] of Object.entries(body)) {
            if (typeof value == 'string') {
                fd.append(key, value);
            } else {
                fd.append(key, JSON.stringify(value));
            }
        }

        if (file) {
            fd.append('profile_picture', file);
        }
        return this.http.post<any>(`${environment.apiUrl}/users/`, fd, {
            headers, params
        });
    }

    getCommunityOnboardingQuestions(id: number): Observable<GenericResponseWithPagination<Question>> {
        return this.http.get<GenericResponseWithPagination<Question>>(`${environment.apiUrl}/communities/${id}/questions/`);
    }

    checkEligibility(id: number, body: QuestionAnswer[]): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/communities/${id}/onboarding/`, {answers: body});
    }

    addUserToCommunity(community_id: number, token?: string): Observable<any> {
        let headers: HttpHeaders = new HttpHeaders({'Authorization': 'Bearer ' + token});
        return this.http.post<any>(`${environment.apiUrl}/users/add-to-community/`, {
            community_id: community_id,
        }, {headers});
    }

    getUserInvite(invitetoken: string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/invites/accept/${invitetoken}/`);
    }

    async setUserWithToken(token: string, hijacked: boolean = false): Promise<unknown> {
        return new Promise((resolve, reject): void => {
            localStorage.setItem('access_token', token);
            this.http.get<any>(`${environment.apiUrl}/users/`).subscribe(r => {
                r.hijacked = hijacked
                localStorage.setItem('currentUser', JSON.stringify(r));
                resolve(r)
            }, e => {
                reject(e)
                console.error(e);
            });
        });
        // Use token received from passthrough page to set the user.
    }


    leaveCommunity(community_id: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/communities/${community_id}/unsubscribe/`, {});
    }

    // profile Questions
    getCommunityProfileQuestions(id?: number): Observable<any> {
        let params: HttpParams = new HttpParams();
        if (id) {
            params = params.append('community_id', id);
        }
        return this.http.get<any>(`${environment.apiUrl}/profile-questions`, {
            params
        });
    }

    getProfileQuestionAnswer(id: number): Observable<any> {
        let params: HttpParams = new HttpParams();
        params = params.append('community_id', id);
        return this.http.get<any>(`${environment.apiUrl}/profile-answers`, {
            params
        });
    }


    postProfileQuestionAnswer(body: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/profile-answers`, body);
    }

}
